import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';

// Gamlir forskotshlekkir (/urval?lykill=...) vísa áfram á /forskot og halda lyklinum
function ForskotRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/forskot${search}`} replace />;
}
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from '@/components/ProtectedRoute';
import { lazy, Suspense } from 'react';
import HomeLoading from '@/components/HomeLoading';
import { getInitialSiteContent } from '@/lib/useSiteContent';
import { Toaster } from '@/components/ui/toaster';
// Add page imports here
import Home from './pages/Home';
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
import Layout from './components/Layout';
const Shop = lazy(() => import('./pages/Shop'));
const Gjafahugmyndir = lazy(() => import('./pages/Gjafahugmyndir'));
const GjafabrefStada = lazy(() => import('./pages/GjafabrefStada'));
const GjafabrefKaupa = lazy(() => import('./pages/GjafabrefKaupa'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Sell = lazy(() => import('./pages/Sell'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Heimsokn = lazy(() => import('./pages/Heimsokn'));
const Postlisti = lazy(() => import('./pages/Postlisti'));
const UmOkkur = lazy(() => import('./pages/UmOkkur'));
const Personuvernd = lazy(() => import('./pages/Personuvernd'));
const Skilmalar = lazy(() => import('./pages/Skilmalar'));
const Spurningar = lazy(() => import('./pages/Spurningar'));
const Draumatreyja = lazy(() => import('./pages/Draumatreya'));
const Stjornbord = lazy(() => import('./pages/Stjornbord'));
const Forskot = lazy(() => import('./pages/Forskot'));
const Reikningur = lazy(() => import('./pages/Reikningur'));
const Pontun = lazy(() => import('./pages/Pontun'));
const Uppahald = lazy(() => import('./pages/Uppahald'));
const Flokkar = lazy(() => import('./pages/Flokkar'));
import { CartProvider } from '@/lib/cartContext';

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    if (window.location.pathname === '/') {
      const initialContent = getInitialSiteContent();
      return <HomeLoading title={initialContent.hero.title} tickerItems={initialContent.ticker?.items || []} />;
    }
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/safnid" element={<Shop />} />
        <Route path="/gjafahugmyndir" element={<Gjafahugmyndir />} />
        <Route path="/gjafabref/stada" element={<GjafabrefStada />} />
        <Route path="/gjafabref" element={<GjafabrefKaupa />} />
        <Route path="/lid/:teamSlug" element={<Shop />} />
        <Route path="/vara/:id" element={<ProductDetail />} />
        <Route path="/seldu" element={<Sell />} />
        <Route path="/heimsokn" element={<Heimsokn />} />
        <Route path="/postlisti" element={<Postlisti />} />
        <Route path="/um-okkur" element={<UmOkkur />} />
        <Route path="/personuvernd" element={<Personuvernd />} />
        <Route path="/skilmalar" element={<Skilmalar />} />
        <Route path="/spurningar" element={<Spurningar />} />
        <Route path="/draumatreyja" element={<Draumatreyja />} />
        <Route path="/uppahald" element={<Uppahald />} />
        <Route path="/flokkar" element={<Flokkar />} />
        <Route path="/forskot" element={<Forskot />} />
        <Route path="/urval" element={<ForskotRedirect />} />
        <Route path="/karfan" element={<Checkout />} />
        <Route path="/pontun" element={<Pontun />} />
        <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} requireAdmin />}>
          <Route path="/stjornbord" element={<Stjornbord />} />
        </Route>
        <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
          <Route path="/reikningur" element={<Reikningur />} />
        </Route>
      </Route>
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <CartProvider>
          <Router>
            <ScrollToTop />
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-border border-t-primary rounded-full animate-spin" /></div>}>
              <AuthenticatedApp />
            </Suspense>
          </Router>
        </CartProvider>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App